import { useState, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import { FileCode, Database, GitBranch, Globe, CheckCircle2, XCircle } from "lucide-react";
import { SiGithub, SiTerraform } from "@icons-pack/react-simple-icons";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { FormField } from "../../components/ui/form_field";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { Tabs, Tab } from "../../components/ui/Tabs";
import { CodeSnippetWithCopy } from "../../components/ui/CodeSnippetWithCopy";
import { useToast } from "../../components/ui/toast";
import { getFormIconClassName } from "@/lib/utils";

interface Workspace {
  name: string;
  slug: string;
}

interface GitHubRepository {
  id: number;
  uuid: string;
  full_name: string;
  owner: string;
  name: string;
}

interface AwsAccount {
  uuid: string;
  display_name: string;
  account_id: string;
  role_arn: string;
  assumer_role_arn: string;
}

interface NewTerraformStateProps {
  workspace: Workspace;
  aws_account: AwsAccount;
  github_repositories: GitHubRepository[];
  aws_regions: string[];
}

interface FormData {
  github_repository_id: string;
  backend_type: string;
  s3_bucket_name: string;
  s3_region: string;
  s3_key: string;
  github_state_filename: string;
}

export default function NewTerraformState({
  workspace,
  aws_account,
  github_repositories,
  aws_regions,
}: NewTerraformStateProps) {
  const { showToast } = useToast();
  const { data: formData, setData, post, processing, errors, transform } = useForm({
    github_repository_id: "",
    backend_type: "s3",
    s3_bucket_name: "",
    s3_region: "",
    s3_key: "",
    github_state_filename: "",
  });
  const [activeTab, setActiveTab] = useState<string>("s3");

  // Transform flat form data to nested structure expected by backend
  transform((data) => {
    const base = {
      github_repository_id: parseInt(data.github_repository_id),
      backend_type: activeTab,
    };

    if (activeTab === 'github') {
      return {
        terraform_state: {
          ...base,
          github_state_filename: data.github_state_filename.trim(),
        },
      };
    } else {
      return {
        terraform_state: {
          ...base,
          s3_bucket_name: data.s3_bucket_name.trim(),
          s3_region: data.s3_region,
          s3_key: data.s3_key.trim(),
        },
      };
    }
  });
  const [terraformSnippet, setTerraformSnippet] = useState<string>("");
  const [iamPolicySnippet, setIamPolicySnippet] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState<string>("");
  const [verificationError, setVerificationError] = useState<string>("");

  const breadcrumbs = [
    { label: "Workspaces", href: "/workspaces" },
    { label: workspace.name, href: `/workspaces/${workspace.slug}` },
    {
      label: aws_account.display_name,
      href: `/workspaces/${workspace.slug}/aws_accounts/${aws_account.uuid}`,
    },
    {
      label: "Settings",
      href: `/workspaces/${workspace.slug}/aws_accounts/${aws_account.uuid}/edit`,
    },
    { label: "Connect Infrastructure State" },
  ];

  const updateField = (field: keyof FormData, value: string) => {
    setData(field, value);
  };

  useEffect(() => {
    if (!formData.s3_bucket_name || !formData.s3_key || !formData.s3_region) {
      setTerraformSnippet("");
      setIamPolicySnippet("");
      return;
    }

    const roleArn = aws_account.role_arn;

    // Sanitize bucket name and S3 key for Terraform resource name (snake_case)
    const sanitizedBucket = formData.s3_bucket_name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_");
    const sanitizedKey = formData.s3_key
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_");
    const resourceName = `infralyst_access_${sanitizedBucket}_${sanitizedKey}`;

    // Sanitize bucket name and S3 key for IAM Sid (PascalCase)
    const toPascalCase = (str: string) =>
      str
        .replace(/[^a-zA-Z0-9]/g, " ")
        .split(" ")
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join("");
    const pascalBucket = toPascalCase(formData.s3_bucket_name);
    const pascalKey = toPascalCase(formData.s3_key);
    const sid = `InfralystReadAccess${pascalBucket}${pascalKey}`;

    // Generate IAM Policy JSON
    const iamPolicy = {
      Version: "2012-10-17",
      Statement: [
        {
          Sid: sid,
          Effect: "Allow",
          Principal: {
            AWS: roleArn,
          },
          Action: ["s3:GetObject", "s3:ListBucket"],
          Resource: [
            `arn:aws:s3:::${formData.s3_bucket_name}`,
            `arn:aws:s3:::${formData.s3_bucket_name}/${formData.s3_key}`,
          ],
        },
      ],
    };
    setIamPolicySnippet(JSON.stringify(iamPolicy, null, 2));

    // Generate Terraform HCL with region-specific provider
    const providerAlias = `infralyst_${formData.s3_region.replace(/-/g, "_")}`;
    const terraformHcl = `# Provider alias for Infralyst S3 bucket access in ${formData.s3_region}
# If you already have this provider alias defined, you can skip this block
provider "aws" {
  alias  = "${providerAlias}"
  region = "${formData.s3_region}"
}

resource "aws_s3_bucket_policy" "${resourceName}" {
  provider = aws.${providerAlias}
  bucket   = "${formData.s3_bucket_name}"

  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid    = "${sid}"
      Effect = "Allow"
      Principal = {
        AWS = "${roleArn}"
      }
      Action = [
        "s3:GetObject",
        "s3:ListBucket"
      ]
      Resource = [
        "arn:aws:s3:::${formData.s3_bucket_name}",
        "arn:aws:s3:::${formData.s3_bucket_name}/${formData.s3_key}"
      ]
    }]
  })
}`;
    setTerraformSnippet(terraformHcl);
  }, [formData.s3_bucket_name, formData.s3_key, formData.s3_region, aws_account.role_arn]);

  useEffect(() => {
    if (isVerified) {
      setIsVerified(false);
      setVerificationSuccess("");
      setVerificationError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.s3_bucket_name, formData.s3_region, formData.s3_key, formData.github_state_filename]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      showToast("error", "Please fix the errors below");
    }
  }, [errors, showToast]);

  const handleCancel = () => {
    window.location.href = `/workspaces/${workspace.slug}/aws_accounts/${aws_account.uuid}/edit`;
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    setVerificationError("");
    setVerificationSuccess("");

    const requestBody = activeTab === 'github'
      ? {
          terraform_state: {
            backend_type: 'github',
            github_repository_id: formData.github_repository_id,
            github_state_filename: formData.github_state_filename.trim(),
          },
        }
      : {
          terraform_state: {
            backend_type: 's3',
            s3_bucket_name: formData.s3_bucket_name.trim(),
            s3_region: formData.s3_region,
            s3_key: formData.s3_key.trim(),
          },
        };

    try {
      const response = await fetch(
        `/workspaces/${workspace.slug}/aws_accounts/${aws_account.uuid}/terraform_states/test_connection`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (result.success) {
        setVerificationSuccess(result.message);
        setIsVerified(true);
        showToast("success", result.message);
      } else {
        setVerificationError(result.error);
        showToast("error", result.error);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setVerificationError("An unexpected error occurred");
      showToast("error", "An unexpected error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  const PolicySnippetCard = () => {
    const policyTabs: Tab[] = [
      {
        id: "terraform",
        label: "Terraform",
        icon: SiTerraform,
        content: terraformSnippet ? (
          <>
            <CodeSnippetWithCopy
              code={terraformSnippet}
              label=""
              ariaLabel="Copy Terraform configuration"
              language="hcl"
              showSyntaxHighlighting={true}
              fontSize="0.8rem"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Add this to your Terraform file where you have the Infralyst module and run <code className="px-1 py-0.5 bg-gray-100 dark:bg-zinc-800 rounded text-xs font-mono">terraform apply</code> to grant read access
            </p>
          </>
        ) : (
          <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900/50 border-gray-200 dark:border-zinc-700 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Fill in the bucket name and key to generate the Terraform snippet
            </p>
          </div>
        ),
      },
      {
        id: "iam",
        label: "IAM Policy",
        icon: FileCode,
        content: iamPolicySnippet ? (
          <>
            <CodeSnippetWithCopy
              code={iamPolicySnippet}
              label=""
              ariaLabel="Copy IAM policy JSON"
              language="json"
              showSyntaxHighlighting={true}
              fontSize="0.8rem"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Add this policy to your S3 bucket to grant Infralyst read access
            </p>
          </>
        ) : (
          <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900/50 border-gray-200 dark:border-zinc-700 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Fill in the bucket name and key to generate the policy snippet
            </p>
          </div>
        ),
      },
    ];

    return (
      <div className="bg-background/50 border rounded-xl p-5 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          S3 Bucket Policy
        </h3>
        <Tabs tabs={policyTabs} defaultTab="terraform" />
      </div>
    );
  };

  const handleCreateState = (e: React.FormEvent) => {
    e.preventDefault();
    post(`/workspaces/${workspace.slug}/aws_accounts/${aws_account.uuid}/terraform_states`);
  };

  const handleVerifyOrCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerified) {
      handleCreateState(e);
    } else {
      handleVerify();
    }
  };

  const s3FormContent = (
    <form id="terraform-state-form" className="space-y-5" onSubmit={handleVerifyOrCreate}>
      <FormField label="AWS region" error={errors.s3_region} id="s3_region">
        <Select
          value={formData.s3_region}
          onValueChange={(value) => updateField("s3_region", value)}
          disabled={isVerifying || processing}
        >
          <SelectTrigger
            id="s3_region"
            leftIcon={
              <Globe className={getFormIconClassName(!!errors.s3_region)} />
            }
            aria-invalid={!!errors.s3_region}
          >
            <SelectValue placeholder="Select region..." />
          </SelectTrigger>
          <SelectContent>
            {aws_regions.map((region) => (
              <SelectItem key={region} value={region}>
                {region}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="S3 bucket name"
        error={errors.s3_bucket_name}
        id="s3_bucket_name"
      >
        <Input
          id="s3_bucket_name"
          type="text"
          value={formData.s3_bucket_name}
          onChange={(e) => updateField("s3_bucket_name", e.target.value)}
          placeholder="my-terraform-state-bucket"
          disabled={isVerifying || processing}
          leftIcon={
            <Database
              className={getFormIconClassName(!!errors.s3_bucket_name)}
            />
          }
          aria-invalid={!!errors.s3_bucket_name}
        />
      </FormField>

      <FormField label="S3 key" error={errors.s3_key} id="s3_key">
        <Input
          id="s3_key"
          type="text"
          value={formData.s3_key}
          onChange={(e) => updateField("s3_key", e.target.value)}
          placeholder="terraform.tfstate"
          disabled={isVerifying || processing}
          leftIcon={
            <FileCode className={getFormIconClassName(!!errors.s3_key)} />
          }
          aria-invalid={!!errors.s3_key}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Path to the state file within the bucket
        </p>
      </FormField>

      <FormField
        label="GitHub repository"
        error={errors.github_repository_id}
        id="github_repository_id"
      >
        <Select
          value={formData.github_repository_id}
          onValueChange={(value) =>
            updateField("github_repository_id", value)
          }
          disabled={isVerifying || processing}
        >
          <SelectTrigger
            id="github_repository_id"
            leftIcon={
              <GitBranch
                className={getFormIconClassName(
                  !!errors.github_repository_id
                )}
              />
            }
            aria-invalid={!!errors.github_repository_id}
          >
            <SelectValue placeholder="Select repository..." />
          </SelectTrigger>
          <SelectContent>
            {github_repositories.map((repo) => (
              <SelectItem key={repo.id} value={repo.id.toString()}>
                {repo.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Repository that manages this terraform state
        </p>
      </FormField>

      <PolicySnippetCard />

      {verificationSuccess && (
        <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                Connection successful
              </h3>
              <p className="text-sm text-green-700 dark:text-green-300">
                {verificationSuccess}
              </p>
            </div>
          </div>
        </div>
      )}

      {verificationError && (
        <div className="p-4 border rounded-lg bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                Connection failed
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                {verificationError}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isVerifying || processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            !formData.s3_bucket_name ||
            !formData.s3_region ||
            !formData.s3_key ||
            !formData.github_repository_id ||
            isVerifying ||
            processing
          }
          isLoading={isVerifying || processing}
          loadingText={isVerifying ? "Verifying..." : "Creating..."}
        >
          {isVerified ? "Create state" : "Verify access"}
        </Button>
      </div>
    </form>
  );

  const githubFormContent = (
    <form id="terraform-state-form" className="space-y-5" onSubmit={handleVerifyOrCreate}>
      <FormField
        label="GitHub repository"
        error={errors.github_repository_id}
        id="github_repository_id"
      >
        <Select
          value={formData.github_repository_id}
          onValueChange={(value) =>
            updateField("github_repository_id", value)
          }
          disabled={isVerifying || processing}
        >
          <SelectTrigger
            id="github_repository_id"
            leftIcon={
              <GitBranch
                className={getFormIconClassName(
                  !!errors.github_repository_id
                )}
              />
            }
            aria-invalid={!!errors.github_repository_id}
          >
            <SelectValue placeholder="Select repository..." />
          </SelectTrigger>
          <SelectContent>
            {github_repositories.map((repo) => (
              <SelectItem key={repo.id} value={repo.id.toString()}>
                {repo.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Repository that manages this terraform state
        </p>
      </FormField>

      <FormField
        label="State filename"
        error={errors.github_state_filename}
        id="github_state_filename"
      >
        <Input
          id="github_state_filename"
          type="text"
          value={formData.github_state_filename}
          onChange={(e) => updateField("github_state_filename", e.target.value)}
          placeholder="terraform.tfstate"
          disabled={isVerifying || processing}
          leftIcon={
            <FileCode
              className={getFormIconClassName(!!errors.github_state_filename)}
            />
          }
          aria-invalid={!!errors.github_state_filename}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Relative path to state file in repository (e.g., terraform.tfstate or terraform/prod.tfstate)
        </p>
      </FormField>

      {verificationSuccess && (
        <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                Connection successful
              </h3>
              <p className="text-sm text-green-700 dark:text-green-300">
                {verificationSuccess}
              </p>
            </div>
          </div>
        </div>
      )}

      {verificationError && (
        <div className="p-4 border rounded-lg bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                Connection failed
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                {verificationError}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isVerifying || processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            !formData.github_repository_id ||
            !formData.github_state_filename ||
            isVerifying ||
            processing
          }
          isLoading={isVerifying || processing}
          loadingText={isVerifying ? "Verifying..." : "Creating..."}
        >
          {isVerified ? "Create state" : "Verify access"}
        </Button>
      </div>
    </form>
  );

  const tabs: Tab[] = [
    {
      id: "s3",
      label: "S3",
      icon: Database,
      content: s3FormContent,
    },
    {
      id: "github",
      label: "GitHub",
      icon: SiGithub,
      content: githubFormContent,
    },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Reset verification state when switching tabs
    setIsVerified(false);
    setVerificationSuccess("");
    setVerificationError("");
  };

  return (
    <Layout
      title="Connect Infrastructure State"
      description="Configure a backend for terraform state matching"
    >
      <section className="px-4 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={breadcrumbs} />

          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-gray-100">
              Connect infrastructure state
            </h1>
            <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
              Configure a backend for terraform state matching
            </p>
          </div>

          <div className="bg-background/70 dark:bg-zinc-900/80 backdrop-blur border shadow-lg rounded-2xl p-6">
            <Tabs tabs={tabs} defaultTab="s3" onChange={handleTabChange} />
          </div>
        </div>
      </section>
    </Layout>
  );
}
